import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { TabPanel, TabView } from "primereact/tabview";
import { Toast } from "primereact/toast";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  appInnerCardStyle,
  appMutedTextStyle,
  appNeutralPanelStyle,
  renderDialogFooter,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { getErrorTitle } from "../../../constants/constants";
import { ROLE_TYPE_CAREGIVER } from "../../../constants/RegisterConstants";
import { RouterAddress } from "../../../constants/RouterAddress";
import Unauthorized from "../../../shared/pages/Unauthorized";
import { canAccessCaregiverRole } from "../utils/caregiverRoleAccess";
import { openUrlInNewTab } from "../../../utils/securityUtils";

const defaultStatusOptions = [
  "Done",
  "Partial Assist",
  "Full Assist",
  "Refused",
  "Not Done",
  "Observed",
];

const adlSupportLevelParameterOptions = [
  "Independent",
  "Supervision",
  "Limited Assistance",
  "Extensive Assistance",
  "Total Assistance",
  "Needs Tracking",
];
const adlSupportLevelOptionKeys = new Set(adlSupportLevelParameterOptions.map((option) => option.toLowerCase()));
const isDuplicateAdlSupportParameter = (parameter: any) => {
  const label = String(parameter?.label || parameter?.parameterId || "").trim().toLowerCase();
  const options = Array.isArray(parameter?.options)
    ? parameter.options.map((option: any) => String(option || "").trim().toLowerCase()).filter(Boolean)
    : [];
  return ["assistance", "support", "support level", "support required"].includes(label)
    && options.length === adlSupportLevelParameterOptions.length
    && new Set(options).size === adlSupportLevelOptionKeys.size
    && options.every((option: string) => adlSupportLevelOptionKeys.has(option));
};
const getAdlChartingParametersForCharting = (parameters: any[]) => (
  (Array.isArray(parameters) ? parameters : []).filter((parameter: any) => !isDuplicateAdlSupportParameter(parameter))
);

const adlChartOutcomeOptions = [
  { label: "Record", value: "recorded" },
  { label: "Not Recorded", value: "not_recorded" },
  { label: "Refused", value: "refused" },
  { label: "Hospitalized", value: "hospitalized" },
  { label: "Family", value: "family" },
];

const adlNoteOnlyActionValues = new Set(["not_recorded", "refused", "family", "hospitalized"]);

const supportLevelOptions = [
  "Independent",
  "Supervision",
  "Limited Assistance",
  "Extensive Assistance",
  "Total Assistance",
  "Needs Tracking",
].map((value) => ({ label: value, value }));

const adlChartDialogStyle: React.CSSProperties = { width: "min(56rem, 96vw)" };

const adlPassFilterOptions = [
  { label: "All passes", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "Charted", value: "charted" },
];

const adlScheduleTimeOptions = Array.from({ length: 24 * 4 }, (_, index) => {
  const hours = Math.floor(index / 4);
  const minutes = (index % 4) * 15;
  const value = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  return { value, label: `${hours % 12 || 12}:${String(minutes).padStart(2, "0")} ${hours < 12 ? "AM" : "PM"}` };
});

const LOS_ANGELES_DATE_ONLY_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Los_Angeles",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const getLosAngelesTodayDateString = () => LOS_ANGELES_DATE_ONLY_FORMATTER.format(new Date());

const LOS_ANGELES_MONTH_VALUE_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Los_Angeles",
  year: "numeric",
  month: "2-digit",
});

const LOS_ANGELES_MONTH_LABEL_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles",
  month: "long",
  year: "numeric",
});

const buildLosAngelesReportMonthOptions = () => {
  const now = new Date();
  const currentYear = Number(new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
  }).format(now));
  const currentMonthIndex = Number(new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    month: "2-digit",
  }).format(now)) - 1;

  return Array.from({ length: 12 }, (_, index) => {
    const targetDate = new Date(Date.UTC(currentYear, currentMonthIndex - index, 15, 12, 0, 0));
    return {
      value: LOS_ANGELES_MONTH_VALUE_FORMATTER.format(targetDate),
      label: LOS_ANGELES_MONTH_LABEL_FORMATTER.format(targetDate),
    };
  });
};

const getLosAngelesYesterdayDateString = () => {
  const todayDateKey = getLosAngelesTodayDateString();
  const match = todayDateKey.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return todayDateKey;
  }
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0));
  date.setUTCDate(date.getUTCDate() - 1);
  return LOS_ANGELES_DATE_ONLY_FORMATTER.format(date);
};

const getLosAngelesSevenDayStartDateString = () => {
  const todayDateKey = getLosAngelesTodayDateString();
  const match = todayDateKey.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return todayDateKey;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0));
  date.setUTCDate(date.getUTCDate() - 6);
  return LOS_ANGELES_DATE_ONLY_FORMATTER.format(date);
};

const formatLosAngelesDateOnlyFromCalendar = (value: Date | null | undefined) => (
  value instanceof Date && !Number.isNaN(value.getTime())
    ? LOS_ANGELES_DATE_ONLY_FORMATTER.format(value)
    : ""
);

const getLosAngelesDateOnlyCalendarValue = (value: string) => {
  const normalized = String(value || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return null;
  }
  const parsed = new Date(normalized + "T12:00:00");
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getLosAngelesHistoryEarliestDateString = () => {
  const todayDateKey = getLosAngelesTodayDateString();
  const match = todayDateKey.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return todayDateKey;
  }
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0));
  date.setUTCMonth(date.getUTCMonth() - 3);
  return LOS_ANGELES_DATE_ONLY_FORMATTER.format(date);
};

const buildEntryDrafts = (publishedTemplate: any, todayEntry: any, todayPassItems?: any[]) => {
  if (Array.isArray(todayPassItems) && todayPassItems.length) {
    return todayPassItems.map((item: any, index: number) => ({
      taskId: String(item?.taskId || `adl-task-${index + 1}`).trim(),
      occurrenceKey: String(item?.occurrenceKey || `${String(item?.taskId || `adl-task-${index + 1}`).trim()}::daily`).trim(),
      occurrenceLabel: String(item?.occurrenceLabel || "Daily Pass").trim(),
      label: String(item?.label || "").trim() || `ADL Task ${index + 1}`,
      supportLevel: String(item?.supportLevel || "").trim(),
      instructions: String(item?.instructions || "").trim(),
      precautions: String(item?.precautions || "").trim(),
      chartStyle: String(item?.chartStyle || "status_only").trim() || "status_only",
      frequencyType: String(item?.frequencyType || "daily").trim() || "daily",
      timesPerDay: Number.isFinite(Number(item?.timesPerDay)) ? Number(item?.timesPerDay) : 1,
      effectiveStartDate: String(item?.effectiveStartDate || "").trim(),
      effectiveEndDate: String(item?.effectiveEndDate || "").trim(),
      scheduledTimes: Array.isArray(item?.scheduledTimes)
        ? item.scheduledTimes.map((value: any) => String(value || "").trim()).filter(Boolean)
        : [],
      scheduledDays: Array.isArray(item?.scheduledDays)
        ? item.scheduledDays.map((value: any) => String(value || "").trim().toLowerCase()).filter(Boolean)
        : [],
      subTypes: Array.isArray(item?.subTypes)
        ? item.subTypes.map((subType: any, subTypeIndex: number) => ({
          subTypeId: String(subType?.subTypeId || `${String(item?.taskId || `adl-task-${index + 1}`).trim()}-subtype-${subTypeIndex + 1}`).trim(),
          code: String(subType?.code || "").trim().toUpperCase(),
          label: String(subType?.label || "").trim(),
          active: subType?.active !== false,
          displayOrder: Number.isFinite(Number(subType?.displayOrder)) ? Number(subType?.displayOrder) : subTypeIndex,
        })).filter((subType: any) => subType.code || subType.label)
        : [],
      chartingParameters: Array.isArray(item?.chartingParameters) ? item.chartingParameters : [],
      parameterValues: item?.parameterValues && typeof item.parameterValues === "object" ? item.parameterValues : {},
      selectedSubTypeCode: String(item?.selectedSubTypeCode || "").trim().toUpperCase(),
      selectedSubTypeCodes: Array.isArray(item?.selectedSubTypeCodes)
        ? item.selectedSubTypeCodes.map((value: any) => String(value || "").trim().toUpperCase()).filter(Boolean)
        : [],
      value: String(item?.value || "").trim(),
      status: String(item?.status || "").trim(),
      note: String(item?.note || "").trim(),
      dateKey: String(item?.dateKey || "").trim(),
      entryVersion: Number.isFinite(Number(item?.entryVersion)) ? Number(item.entryVersion) : 0,
      recordedAt: String(item?.recordedAt || "").trim(),
      statusHistory: Array.isArray(item?.statusHistory) ? item.statusHistory : [],
      chartTimeMode: String(item?.chartTimeMode || "current").trim() || "current",
      exactChartTime: String(item?.exactChartTime || "").trim(),
      statusOptions: Array.isArray(item?.statusOptions) && item.statusOptions.length
        ? item.statusOptions.map((option: any) => String(option || "").trim()).filter(Boolean)
        : defaultStatusOptions,
    }));
  }
  const entryByTaskId = new Map<string, any>(
    (Array.isArray(todayEntry?.taskEntries) ? todayEntry.taskEntries : []).map((entry: any) => [String(entry?.taskId || "").trim(), entry]),
  );
  return (Array.isArray(publishedTemplate?.tasks) ? publishedTemplate.tasks : []).map((task: any, index: number) => {
    const taskId = String(task?.taskId || `adl-task-${index + 1}`).trim();
    const existingEntry = entryByTaskId.get(taskId);
    return {
      taskId,
      occurrenceKey: String(existingEntry?.occurrenceKey || `${taskId}::daily`).trim(),
      occurrenceLabel: String(existingEntry?.occurrenceLabel || "Daily Pass").trim(),
      label: String(task?.label || "").trim() || `ADL Task ${index + 1}`,
      supportLevel: String(task?.supportLevel || "").trim(),
      instructions: String(task?.instructions || "").trim(),
      precautions: String(task?.precautions || "").trim(),
      chartStyle: String(task?.chartStyle || "status_only").trim() || "status_only",
      frequencyType: String(task?.frequencyType || "daily").trim() || "daily",
      timesPerDay: Number.isFinite(Number(task?.timesPerDay)) ? Number(task?.timesPerDay) : 1,
      effectiveStartDate: String(task?.effectiveStartDate || "").trim(),
      effectiveEndDate: String(task?.effectiveEndDate || "").trim(),
      scheduledTimes: Array.isArray(task?.scheduledTimes)
        ? task.scheduledTimes.map((value: any) => String(value || "").trim()).filter(Boolean)
        : [],
      scheduledDays: Array.isArray(task?.scheduledDays)
        ? task.scheduledDays.map((value: any) => String(value || "").trim().toLowerCase()).filter(Boolean)
        : [],
      subTypes: Array.isArray(task?.subTypes)
        ? task.subTypes.map((subType: any, subTypeIndex: number) => ({
          subTypeId: String(subType?.subTypeId || `${taskId}-subtype-${subTypeIndex + 1}`).trim(),
          code: String(subType?.code || "").trim().toUpperCase(),
          label: String(subType?.label || "").trim(),
          active: subType?.active !== false,
          displayOrder: Number.isFinite(Number(subType?.displayOrder)) ? Number(subType?.displayOrder) : subTypeIndex,
        })).filter((subType: any) => subType.code || subType.label)
        : [],
      chartingParameters: Array.isArray(task?.chartingParameters) ? task.chartingParameters : [],
      parameterValues: existingEntry?.parameterValues && typeof existingEntry.parameterValues === "object" ? existingEntry.parameterValues : {},
      selectedSubTypeCode: String(existingEntry?.subTypeCode || "").trim().toUpperCase(),
      selectedSubTypeCodes: Array.isArray(existingEntry?.subTypeCodes)
        ? existingEntry.subTypeCodes.map((value: any) => String(value || "").trim().toUpperCase()).filter(Boolean)
        : [],
      value: String(existingEntry?.value || "").trim(),
      status: String(existingEntry?.status || "").trim(),
      note: String(existingEntry?.note || "").trim(),
      dateKey: String(todayEntry?.dateKey || "").trim(),
      entryVersion: Number.isFinite(Number(todayEntry?.version)) ? Number(todayEntry.version) : 0,
      recordedAt: String(existingEntry?.recordedAt || "").trim(),
      statusHistory: Array.isArray(existingEntry?.statusHistory) ? existingEntry.statusHistory : [],
      chartTimeMode: String(existingEntry?.chartTimeMode || "current").trim() || "current",
      exactChartTime: String(existingEntry?.exactChartTime || "").trim(),
      statusOptions: Array.isArray(task?.statusOptions) && task.statusOptions.length
        ? task.statusOptions.map((option: any) => String(option || "").trim()).filter(Boolean)
        : defaultStatusOptions,
    };
  });
};

type CaregiverResidentAdlPageProps = {
  sharedMode?: boolean;
};

export const CaregiverResidentAdlPageContent = ({ sharedMode = false }: CaregiverResidentAdlPageProps) => {
  const { residentId, homeId } = useParams();
  const navigate = useNavigate();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const toast = useRef<Toast>(null);

  const userRoleType = authStore.getUserRoleType();
  const isAdminUser = authStore.isAdmin();
  const canUseCaregiverWorkspace = sharedMode
    ? Boolean(userRoleType) || isAdminUser
    : canAccessCaregiverRole(authStore);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showChartEntryDialog, setShowChartEntryDialog] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [historyDateKey, setHistoryDateKey] = useState(getLosAngelesYesterdayDateString());
  const [historyStatusFilter, setHistoryStatusFilter] = useState<"all" | "missed">("all");
  const [historyEntryDrafts, setHistoryEntryDrafts] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [todayPassFilter, setTodayPassFilter] = useState<"all" | "pending" | "charted">("all");
  const [mobileAdlDetails, setMobileAdlDetails] = useState<Record<string, boolean>>({});
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [exportingReport, setExportingReport] = useState(false);
  const [selectedReportMonth, setSelectedReportMonth] = useState(LOS_ANGELES_MONTH_VALUE_FORMATTER.format(new Date()));
  const [reportRangeMode, setReportRangeMode] = useState<"monthly" | "last7Days" | "custom">("monthly");
  const [reportStartDate, setReportStartDate] = useState(getLosAngelesSevenDayStartDateString());
  const [reportEndDate, setReportEndDate] = useState(getLosAngelesTodayDateString());
  const [blankAdlReport, setBlankAdlReport] = useState(false);
  const [adlData, setAdlData] = useState<any>(null);
  const [entryDrafts, setEntryDrafts] = useState<any[]>([]);
  const [chartEntryDraft, setChartEntryDraft] = useState<any>(null);
  const [applyHospitalizedToAllForDay, setApplyHospitalizedToAllForDay] = useState(false);
  const resolvedHomeId = String(homeId || adlData?.homeId || "").trim();
  const reportMonthOptions = useMemo(() => buildLosAngelesReportMonthOptions(), []);

  const filteredEntryDrafts = useMemo(() => {
    if (todayPassFilter === "all") {
      return entryDrafts;
    }
    return entryDrafts.filter((entry: any) => {
      const isCharted = Boolean(String(entry?.status || "").trim());
      return todayPassFilter === "charted" ? isCharted : !isCharted;
    });
  }, [entryDrafts, todayPassFilter]);

  const groupedTodayEntryDrafts = useMemo(() => {
    const grouped = new Map<string, any>();
    filteredEntryDrafts.forEach((entry: any) => {
      const taskId = String(entry?.taskId || entry?.occurrenceKey || "").trim();
      const existing = grouped.get(taskId);
      if (existing) {
        existing.occurrences.push(entry);
      } else {
        grouped.set(taskId, { ...entry, occurrences: [entry] });
      }
    });
    return Array.from(grouped.values());
  }, [filteredEntryDrafts]);

  const adlListRows = useMemo(() => {
    const rows = new Map<string, any>();
    entryDrafts.forEach((entry: any) => {
      const taskId = String(entry?.taskId || entry?.occurrenceKey || "").trim();
      if (!rows.has(taskId)) rows.set(taskId, entry);
    });
    return Array.from(rows.values());
  }, [entryDrafts]);

  const loadAdlTracking = async (requestedHistoryDateKey = historyDateKey) => {
    if (!residentId) {
      return;
    }
    setLoading(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.getCaregiverResidentAdlTracking(residentId, { historyDateKey: requestedHistoryDateKey });
      setAdlData(response || null);
      const initialEntryDrafts = buildEntryDrafts(response?.publishedTemplate, response?.todayEntry, response?.todayPassItems);
      setEntryDrafts(initialEntryDrafts);
      setHistoryDateKey(String(response?.historyDateKey || requestedHistoryDateKey).trim());
      setHistoryEntryDrafts(buildEntryDrafts(response?.publishedTemplate, response?.historyEntry, response?.historyPassItems));
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to load ADL tracking.",
        life: 3500,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAdlTracking();
  }, [residentId]);

  const loadAdlHistory = async (requestedHistoryDateKey: string) => {
    if (!residentId) {
      return;
    }
    setLoadingHistory(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.getCaregiverResidentAdlTracking(residentId, { historyDateKey: requestedHistoryDateKey });
      setAdlData((currentData: any) => ({ ...(currentData || {}), ...(response || {}) }));
      setHistoryDateKey(String(response?.historyDateKey || requestedHistoryDateKey).trim());
      setHistoryEntryDrafts(buildEntryDrafts(response?.publishedTemplate, response?.historyEntry, response?.historyPassItems));
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to load past ADL entries.",
        life: 3500,
      });
      setHistoryEntryDrafts([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (activeTabIndex === 2 && adlData) {
      void loadAdlHistory(historyDateKey);
    }
  }, [activeTabIndex, historyDateKey]);

  const updateEntryDraft = (taskId: string, field: string, value: any) => {
    setEntryDrafts((currentEntries) => currentEntries.map((entry) => (
      String(entry?.occurrenceKey || entry?.taskId || "").trim() === taskId
        ? {
          ...entry,
          [field]: value,
        }
        : entry
    )));
  };

  const handleSave = async (options?: {
    silent?: boolean;
    nextEntries?: any[];
    dateKey?: string;
    correctionMode?: boolean;
    targetOccurrenceKey?: string;
    expectedVersion?: number;
  }) => {
    if (!residentId) {
      return;
    }
    const nextEntries = Array.isArray(options?.nextEntries) ? options?.nextEntries : entryDrafts;
    const entriesToSave = Array.isArray(nextEntries)
      ? nextEntries.filter((entry: any) => {
        const value = String(entry?.value || "").trim();
        return value && (adlNoteOnlyActionValues.has(value) || String(entry?.status || "").trim());
      })
      : [];
    setSaving(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.saveCaregiverResidentAdlEntry({
        residentId,
        dateKey: String(options?.dateKey || adlData?.todayDateKey || "").trim(),
        taskEntries: entriesToSave,
        correctionMode: options?.correctionMode,
        targetOccurrenceKey: options?.targetOccurrenceKey,
        expectedVersion: options?.expectedVersion,
      });
      const refreshedEntryDrafts = buildEntryDrafts(response?.publishedTemplate || adlData?.publishedTemplate, response?.todayEntry || response, response?.todayPassItems);
      setAdlData((current: any) => ({
        ...(current || {}),
        publishedTemplate: response?.publishedTemplate || current?.publishedTemplate,
        todayEntry: response?.todayEntry || response,
        todayPassItems: response?.todayPassItems || current?.todayPassItems,
      }));
      setEntryDrafts(refreshedEntryDrafts);
      if (options?.correctionMode) {
        setHistoryDateKey(String(response?.historyDateKey || options?.dateKey || historyDateKey).trim());
        setHistoryEntryDrafts(buildEntryDrafts(response?.publishedTemplate || adlData?.publishedTemplate, response?.historyEntry, response?.historyPassItems));
      }
      if (!options?.silent) {
        toast.current?.show({
          severity: "success",
          summary: options?.correctionMode ? "Past ADL Entry Updated" : "ADL Saved",
          detail: options?.correctionMode ? "The ADL entry was corrected." : "Today's ADL charting was recorded.",
          life: 2500,
        });
      }
    } catch (error: any) {
      if (!options?.silent) {
        toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to save ADL charting.", life: 3500 });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!residentId || !resolvedHomeId) {
      return;
    }
    setExportingReport(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.generateResidentAdlReportDocument({
        residentId,
        homeId: resolvedHomeId,
        reportMonth: selectedReportMonth,
        reportRange: reportRangeMode,
        reportStartDate: reportRangeMode === "custom" ? reportStartDate : undefined,
        reportEndDate: reportRangeMode === "custom" ? reportEndDate : undefined,
        blankReport: blankAdlReport,
      });
      if (response?.documentUrl) {
        openUrlInNewTab(String(response.documentUrl));
      }
      toast.current?.show({
        severity: "success",
        summary: "ADL Report Ready",
        detail: "The ADL report document opened in a new tab.",
        life: 2500,
      });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to generate ADL report.", life: 3500 });
    } finally {
      setExportingReport(false);
    }
  };

  const openChartEntryDialog = (rowData: any, presetValue?: string) => {
    const rowDateKey = String(rowData?.dateKey || adlData?.todayDateKey || "").trim();
    const correctionMode = rowData?.correctionMode === true;
    setChartEntryDraft({
      occurrenceKey: String(rowData?.occurrenceKey || rowData?.taskId || "").trim(),
      label: String(rowData?.label || "").trim(),
      occurrenceLabel: String(rowData?.occurrenceLabel || "Daily Pass").trim(),
      actionLabel: adlChartOutcomeOptions.find((option) => option.value === String(presetValue || rowData?.value || "").trim())?.label || "Record",
      chartStyle: String(rowData?.chartStyle || "status_only").trim() || "status_only",
      subTypes: Array.isArray(rowData?.subTypes) ? rowData.subTypes : [],
      statusOptions: Array.isArray(rowData?.statusOptions) ? rowData.statusOptions : defaultStatusOptions,
      value: String(presetValue || rowData?.value || "").trim() || "recorded",
      selectedSubTypeCode: String(rowData?.selectedSubTypeCode || "").trim().toUpperCase(),
      selectedSubTypeCodes: Array.isArray(rowData?.selectedSubTypeCodes) ? rowData.selectedSubTypeCodes : [],
      chartingParameters: getAdlChartingParametersForCharting(rowData?.chartingParameters),
      parameterValues: rowData?.parameterValues && typeof rowData.parameterValues === "object" ? rowData.parameterValues : {},
      status: String(rowData?.status || rowData?.supportLevel || "").trim(),
      note: String(rowData?.note || "").trim(),
      dateKey: rowDateKey,
      entryVersion: Number.isFinite(Number(rowData?.entryVersion)) ? Number(rowData.entryVersion) : 0,
      correctionMode,
      chartTimeMode: String(rowData?.chartTimeMode || (correctionMode && rowDateKey !== getLosAngelesTodayDateString() ? "scheduled" : "current")).trim() || "current",
      exactChartTime: String(rowData?.exactChartTime || "").trim(),
    });
    setApplyHospitalizedToAllForDay(false);
    setShowChartEntryDialog(true);
  };

  const closeChartEntryDialog = () => {
    setShowChartEntryDialog(false);
    setChartEntryDraft(null);
  };

  const applyChartEntryDialog = () => {
    const occurrenceKey = String(chartEntryDraft?.occurrenceKey || "").trim();
    if (!occurrenceKey) {
      closeChartEntryDialog();
      return;
    }
    const correctionMode = chartEntryDraft?.correctionMode === true;
    const dateKey = String(chartEntryDraft?.dateKey || adlData?.todayDateKey || "").trim();
    const value = String(chartEntryDraft?.value || "").trim();
    const noteOnlyAction = adlNoteOnlyActionValues.has(value);
    const supportRequired = String(chartEntryDraft?.status || "").trim();
    const note = String(chartEntryDraft?.note || "").trim();
    const noteRequired = value !== "recorded";
    const chartingParameters = Array.isArray(chartEntryDraft?.chartingParameters) ? chartEntryDraft.chartingParameters : [];
    const parameterValues = chartEntryDraft?.parameterValues && typeof chartEntryDraft.parameterValues === "object" ? chartEntryDraft.parameterValues : {};
    const chartTimeMode = String(chartEntryDraft?.chartTimeMode || "current").trim();
    const exactChartTime = String(chartEntryDraft?.exactChartTime || "").trim();
    const missingParameter = chartingParameters.find((parameter: any) => {
      const parameterId = String(parameter?.parameterId || "").trim();
      const parameterValue = parameterValues[parameterId];
      return !parameterId || parameterValue === null || parameterValue === undefined || String(parameterValue).trim() === "";
    });
    if (!dateKey || !value || (!noteOnlyAction && !supportRequired) || (noteRequired && !note) || (!noteOnlyAction && missingParameter) || (!noteOnlyAction && chartTimeMode === "exact" && !exactChartTime)) {
      const detail = !dateKey ? "A chart date is required." : !value ? "Select an action before saving." : !noteOnlyAction && !supportRequired ? "Select the support required before saving." : noteRequired && !note ? "Note is required for this action." : !noteOnlyAction && missingParameter ? `Select ${String(missingParameter?.label || "all charting parameters")} before saving.` : "Select an exact charting time before saving.";
      toast.current?.show({ severity: "warn", summary: "Required information", detail, life: 3000 });
      return;
    }
    const chartFields = {
      value,
      selectedSubTypeCode: noteOnlyAction ? "" : String(chartEntryDraft?.selectedSubTypeCode || "").trim().toUpperCase(),
      selectedSubTypeCodes: noteOnlyAction ? [] : (Array.isArray(chartEntryDraft?.selectedSubTypeCodes) ? chartEntryDraft.selectedSubTypeCodes : []),
      parameterValues: noteOnlyAction ? {} : parameterValues,
      status: noteOnlyAction ? "" : supportRequired,
      note,
      chartTimeMode: noteOnlyAction ? "current" : chartTimeMode,
      exactChartTime: noteOnlyAction ? "" : exactChartTime,
    };
    const bulkHospitalizedFields = {
      value,
      status: supportRequired,
      note,
      chartTimeMode,
      exactChartTime,
    };
    const sourceEntries = correctionMode ? historyEntryDrafts : entryDrafts;
    const nextEntries = sourceEntries.map((entry) => (
      (applyHospitalizedToAllForDay && value === "hospitalized") || String(entry?.occurrenceKey || entry?.taskId || "").trim() === occurrenceKey
        ? {
          ...entry,
          ...(applyHospitalizedToAllForDay && value === "hospitalized" ? bulkHospitalizedFields : chartFields),
        }
        : entry
    ));
    if (correctionMode) {
      setHistoryEntryDrafts(nextEntries);
    } else {
      setEntryDrafts(nextEntries);
    }
    const updatedEntry = nextEntries.find((entry) => String(entry?.occurrenceKey || entry?.taskId || "").trim() === occurrenceKey);
    const entriesToSave = applyHospitalizedToAllForDay && value === "hospitalized"
      ? nextEntries.filter((entry) => {
        const entryValue = String(entry?.value || "").trim();
        return entryValue && (adlNoteOnlyActionValues.has(entryValue) || String(entry?.status || "").trim());
      })
      : updatedEntry ? [updatedEntry] : [];
    void handleSave({
      nextEntries: entriesToSave,
      dateKey,
      correctionMode,
      targetOccurrenceKey: correctionMode ? occurrenceKey : undefined,
      expectedVersion: correctionMode ? Number(chartEntryDraft?.entryVersion || 0) : undefined,
    });
    closeChartEntryDialog();
  };

  const buildSubtypeSelectionSummary = (rowData: any) => {
    const selectedCodes = Array.isArray(rowData?.selectedSubTypeCodes) && rowData.selectedSubTypeCodes.length
      ? rowData.selectedSubTypeCodes
      : (String(rowData?.selectedSubTypeCode || "").trim() ? [String(rowData?.selectedSubTypeCode || "").trim()] : []);
    if (!selectedCodes.length) {
      return "";
    }
    const subTypes = Array.isArray(rowData?.subTypes) ? rowData.subTypes : [];
    return selectedCodes.map((code: string) => {
      const matched = subTypes.find((subType: any) => String(subType?.code || "").trim().toUpperCase() === String(code || "").trim().toUpperCase());
      return matched ? `${matched.code} ${matched.label}`.trim() : String(code || "").trim().toUpperCase();
    }).filter(Boolean).join(", ");
  };

  const scheduleSummary = (rowData: any) => {
    const scheduledTimes = Array.isArray(rowData?.scheduledTimes) ? rowData.scheduledTimes.join(", ") : "";
    const scheduledDays = Array.isArray(rowData?.scheduledDays) && rowData.scheduledDays.length
      ? rowData.scheduledDays.map((value: string) => value.slice(0, 3).toUpperCase()).join(", ")
      : "";
    const effectiveStartDate = String(rowData?.effectiveStartDate || "").trim();
    const effectiveEndDate = String(rowData?.effectiveEndDate || "").trim();
    if (scheduledTimes || scheduledDays) {
      return [scheduledTimes, scheduledDays, effectiveStartDate ? `Start ${effectiveStartDate}` : "", effectiveEndDate ? `Exp ${effectiveEndDate}` : ""].filter(Boolean).join(" | ");
    }
    const frequencyType = String(rowData?.frequencyType || "daily").trim();
    if (frequencyType === "times_per_day") {
      const timesPerDay = Number(rowData?.timesPerDay || 1);
      return [`${timesPerDay} time${timesPerDay === 1 ? "" : "s"} per day`, effectiveStartDate ? `Start ${effectiveStartDate}` : "", effectiveEndDate ? `Exp ${effectiveEndDate}` : ""].filter(Boolean).join(" | ");
    }
    if (frequencyType === "shift_based") {
      return ["Each shift", effectiveStartDate ? `Start ${effectiveStartDate}` : "", effectiveEndDate ? `Exp ${effectiveEndDate}` : ""].filter(Boolean).join(" | ");
    }
    return ["Daily", effectiveStartDate ? `Start ${effectiveStartDate}` : "", effectiveEndDate ? `Exp ${effectiveEndDate}` : ""].filter(Boolean).join(" | ");
  };

  const supportBody = (rowData: any) => (
    <div style={{ lineHeight: 1.5 }}>
      <div style={{ fontWeight: 700, color: "#0f172a" }}>{rowData?.supportLevel || "Not set"}</div>
      <div style={{ ...appMutedTextStyle, marginTop: "0.25rem", whiteSpace: "pre-wrap" }}>
        {rowData?.instructions || "No additional instructions."}
      </div>
      <div className="flex gap-2 flex-wrap mt-2">
        {Array.isArray(rowData?.subTypes) && rowData.subTypes.length ? rowData.subTypes.map((subType: any) => (
          <Tag
            key={String(subType?.subTypeId || `${subType?.code || ""}-${subType?.label || ""}`)}
            value={`${String(subType?.code || "").trim().toUpperCase()} ${String(subType?.label || "").trim()}`.trim()}
            severity="info"
          />
        )) : (
          <Tag value="No subtype options" severity="secondary" />
        )}
      </div>
      <div style={{ ...appMutedTextStyle, marginTop: "0.5rem" }}>
        {scheduleSummary(rowData)}
      </div>
      {!!String(rowData?.precautions || "").trim() && (
        <div style={{ ...appMutedTextStyle, marginTop: "0.25rem", whiteSpace: "pre-wrap" }}>
          Precautions: {String(rowData?.precautions || "").trim()}
        </div>
      )}
    </div>
  );

  const renderMobileAdlDetail = (rowData: any, detail: "instructions" | "precautions") => {
    const value = String(rowData?.[detail] || "").trim();
    if (!value) return null;

    const taskKey = String(rowData?.taskId || rowData?.adlItemId || rowData?.occurrenceKey || rowData?.label || "adl-task").trim();
    const visibilityKey = `${taskKey}:${detail}`;
    const isVisible = mobileAdlDetails[visibilityKey] === true;
    const label = detail === "precautions" ? "Risks & Precautions" : "Instructions";
    const detailStyle = {
      ...appMutedTextStyle,
      fontSize: "0.82rem",
      whiteSpace: "pre-wrap" as const,
      ...(detail === "precautions" ? { color: "#9a3412" } : {}),
    };

    return (
      <>
        <div className="hidden md:block mt-1" style={detailStyle}>
          <strong style={detail === "instructions" ? { color: "#334155" } : undefined}>{label}:</strong> {value}
        </div>
        <div className="block md:hidden mt-2">
          <Button
            type="button"
            label={`${isVisible ? "Hide" : "Show"} ${label}`}
            size="small"
            severity={detail === "precautions" ? "warning" : "secondary"}
            outlined
            rounded
            onClick={() => setMobileAdlDetails((current) => ({ ...current, [visibilityKey]: !isVisible }))}
          />
          {isVisible ? (
            <div style={{ ...detailStyle, marginTop: "0.5rem" }}>
              <strong style={detail === "instructions" ? { color: "#334155" } : undefined}>{label}:</strong> {value}
            </div>
          ) : null}
        </div>
      </>
    );
  };

  const adlTaskBody = (rowData: any) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontWeight: 700, color: "#0f172a" }}>{rowData?.label || "ADL Task"}</div>
      <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
        {scheduleSummary(rowData)}
      </div>
      {Array.isArray(rowData?.subTypes) && rowData.subTypes.length ? (
        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
          <strong style={{ color: "#334155" }}>Options:</strong> {rowData.subTypes.map((subType: any) => `${String(subType?.code || "").trim().toUpperCase()} ${String(subType?.label || "").trim()}`.trim()).filter(Boolean).join(", ")}
        </div>
      ) : null}
      {renderMobileAdlDetail(rowData, "instructions")}
      {renderMobileAdlDetail(rowData, "precautions")}
    </div>
  );

  const passTaskBody = (rowData: any) => (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontWeight: 700, color: "#0f172a" }}>{rowData?.label || "ADL Task"}</div>
      <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
        {String(rowData?.occurrenceLabel || "").trim() && String(rowData?.occurrenceLabel || "").trim() !== "Daily Pass"
          ? String(rowData.occurrenceLabel).trim()
          : scheduleSummary(rowData)}
      </div>
      {rowData?.supportLevel ? (
        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          <strong style={{ color: "#334155" }}>Support:</strong> {rowData.supportLevel}
        </div>
      ) : null}
      {renderMobileAdlDetail(rowData, "instructions")}
      {renderMobileAdlDetail(rowData, "precautions")}
    </div>
  );

  const formatAdlOccurrenceLabel = (rowData: any) => {
    const label = String(rowData?.occurrenceLabel || "").trim();
    const match = label.match(/^(\d{1,2}):(\d{2})(?:\s*([ap]m))?$/i);
    if (!match) return label || "Scheduled";
    let hours = Number(match[1]);
    const minutes = Number(match[2]);
    const meridiem = String(match[3] || "").toLowerCase();
    if (meridiem === "pm" && hours < 12) hours += 12;
    if (meridiem === "am" && hours === 12) hours = 0;
    return hours <= 23 && minutes <= 59
      ? String(hours % 12 || 12) + ":" + String(minutes).padStart(2, "0") + " " + (hours < 12 ? "AM" : "PM")
      : label;
  };

  const parametersBody = (rowData: any) => {
    const parameters = getAdlChartingParametersForCharting(rowData?.chartingParameters);
    if (!parameters.length) return <span style={appMutedTextStyle}>None</span>;
    return (
      <div className="flex flex-column gap-1">
        {parameters.map((parameter: any) => (
          <div key={String(parameter?.parameterId || parameter?.label || "parameter")} style={{ fontSize: "0.82rem" }}>
            <strong style={{ color: "#334155" }}>{parameter?.label || "Parameter"}:</strong>{" "}
            {parameter?.type === "number" ? "Enter a number" : (Array.isArray(parameter?.options) ? parameter.options.join(", ") : "Single select")}
          </div>
        ))}
      </div>
    );
  };

  const isAdlChartOutsideOneHourWindow = (rowData: any) => {
    const match = String(rowData?.occurrenceLabel || "").trim().match(/^(\d{1,2}):(\d{2})$/);
    const rowDateKey = String(rowData?.dateKey || adlData?.todayDateKey || "").trim();
    if (!match || rowDateKey !== getLosAngelesTodayDateString()) return false;
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: "America/Los_Angeles", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date());
    const now = Number(parts.find((part) => part.type === "hour")?.value || 0) * 60 + Number(parts.find((part) => part.type === "minute")?.value || 0);
    return Math.abs(now - (Number(match[1]) * 60 + Number(match[2]))) > 60;
  };

  const getAdlChartTimingDifferenceHours = (rowData: any) => {
    const match = String(rowData?.occurrenceLabel || "").trim().match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return 0;
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: "America/Los_Angeles", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date());
    const now = Number(parts.find((part) => part.type === "hour")?.value || 0) * 60 + Number(parts.find((part) => part.type === "minute")?.value || 0);
    return Math.abs(now - (Number(match[1]) * 60 + Number(match[2]))) / 60;
  };

  const isAdlOccurrenceScheduledInFuture = (rowData: any) => {
    const match = String(rowData?.occurrenceLabel || "").trim().match(/^(\d{1,2}):(\d{2})$/);
    const rowDateKey = String(rowData?.dateKey || adlData?.todayDateKey || "").trim();
    if (!match || rowDateKey !== getLosAngelesTodayDateString()) return false;
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date());
    const currentMinutes = (Number(parts.find((part) => part.type === "hour")?.value || 0) * 60)
      + Number(parts.find((part) => part.type === "minute")?.value || 0);
    return currentMinutes < (Number(match[1]) * 60) + Number(match[2]);
  };

  const getTodayStatusMeta = (rowData: any) => {
    const chartOutcome = String(rowData?.value || "").trim().toLowerCase();
    const statusSummary = String(rowData?.status || "").trim();
    const scheduledLabel = String(rowData?.occurrenceLabel || "").trim();
    const scheduledTimeMatch = scheduledLabel.match(/^(\d{1,2}):(\d{2})(?:\s*([ap]m))?$/i);
    let scheduledMinutes = -1;
    if (scheduledTimeMatch) {
      let hours = Number(scheduledTimeMatch[1]);
      const minutes = Number(scheduledTimeMatch[2]);
      const meridiem = String(scheduledTimeMatch[3] || "").toLowerCase();
      if (meridiem === "pm" && hours < 12) hours += 12;
      if (meridiem === "am" && hours === 12) hours = 0;
      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) scheduledMinutes = (hours * 60) + minutes;
    }
    const currentParts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles", year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).formatToParts(new Date());
    const currentDateKey = `${currentParts.find((part) => part.type === "year")?.value}-${currentParts.find((part) => part.type === "month")?.value}-${currentParts.find((part) => part.type === "day")?.value}`;
    const currentMinutes = (Number(currentParts.find((part) => part.type === "hour")?.value || 0) * 60)
      + Number(currentParts.find((part) => part.type === "minute")?.value || 0);
    const overdueFallback = !statusSummary && !chartOutcome && scheduledMinutes >= 0
      && String(adlData?.todayDateKey || "").trim() === currentDateKey && currentMinutes >= scheduledMinutes;
    if (rowData?.missed === true || overdueFallback || statusSummary.toLowerCase() === "missed" || chartOutcome === "missed") return { label: "Missed", severity: "danger" };
    if (chartOutcome === "recorded") return { label: "Recorded", severity: "success" };
    if (chartOutcome === "not_recorded") return { label: "Not recorded", severity: "warning" };
    if (chartOutcome === "refused") return { label: "Refused", severity: "danger" };
    if (chartOutcome === "family") return { label: "Family", severity: "info" };
    if (chartOutcome === "hospitalized") return { label: "Hospitalized", severity: "warning" };
    return { label: "Pending", severity: "secondary" };
  };

  const chartSummaryCardBody = (rowData: any) => {
    const statusMeta = getTodayStatusMeta(rowData);
    const subtypeSummary = buildSubtypeSelectionSummary(rowData);
    const noteSummary = String(rowData?.note || "").trim();
    const statusMessage = String(rowData?.statusMessage || "").trim()
      || (statusMeta.label === "Missed" ? "No charted entry was saved for this scheduled ADL pass." : "");
    return (
      <div style={{ minWidth: 0, border: "1px solid #e2e8f0", borderRadius: "12px", padding: "0.65rem 0.75rem", background: "#fff", lineHeight: 1.55 }}>
        <div className="flex gap-2 flex-wrap">
          <Tag value={statusMeta.label + " " + formatAdlOccurrenceLabel(rowData)} severity={statusMeta.severity as any} rounded />
        </div>
        {statusMessage ? <div style={{ ...appMutedTextStyle, marginTop: "0.35rem", fontSize: "0.82rem", color: statusMeta.label === "Missed" ? "#b91c1c" : undefined }}>{statusMessage}</div> : null}
        {subtypeSummary ? <div style={{ ...appMutedTextStyle, marginTop: "0.35rem" }}>{subtypeSummary}</div> : null}
        {noteSummary ? <div style={{ ...appMutedTextStyle, marginTop: "0.2rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{noteSummary}</div> : null}
      </div>
    );
  };

  const chartSummaryBody = (rowData: any) => {
    const occurrences = Array.isArray(rowData?.occurrences) ? rowData.occurrences : [rowData];
    return (
      <div className="flex flex-column gap-2" style={{ minWidth: "17rem" }}>
        {occurrences.map((occurrence: any) => (
          <div key={String(occurrence?.occurrenceKey || occurrence?.taskId || "adl-occurrence")}>
            {chartSummaryCardBody(occurrence)}
          </div>
        ))}
      </div>
    );
  };

  const chartActionCardBody = (rowData: any) => {
    const currentValue = String(rowData?.value || "").trim().toLowerCase();
    if (isAdlOccurrenceScheduledInFuture(rowData)) {
      return (
        <div style={{ minWidth: "14rem", border: "1px solid #fde68a", background: "#fffbeb", borderRadius: "12px", padding: "0.7rem 0.8rem", color: "#92400e", fontSize: "0.82rem", lineHeight: 1.45 }}>
          This ADL item is scheduled for {rowData?.occurrenceLabel || "the scheduled time"} and cannot be charted until then.
        </div>
      );
    }
    const actions = [
      { label: "Record", value: "recorded", color: "#166534", background: "#dcfce7", border: "#16a34a" },
      { label: "Not Recorded", value: "not_recorded", color: "#991b1b", background: "#fee2e2", border: "#dc2626" },
      { label: "Refused", value: "refused", color: "#991b1b", background: "#fee2e2", border: "#b91c1c" },
      { label: "Family", value: "family", color: "#1d4ed8", background: "#dbeafe", border: "#2563eb" },
      { label: "Hospitalized", value: "hospitalized", color: "#9a3412", background: "#fef3c7", border: "#d97706" },
    ];
    const visibleActions = currentValue ? actions.filter((action) => action.value === currentValue) : actions;
    return (
      <div style={{ minWidth: "18rem", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "0.65rem 0.75rem", background: "#fff" }}>
        <div className="mb-2" style={{ fontSize: "0.8rem", fontWeight: 700, color: "#475569" }}>{formatAdlOccurrenceLabel(rowData)}</div>
        <div className="flex gap-2 flex-wrap">
          {visibleActions.map((action) => (
            <Tag
              key={action.value}
              value={action.label}
              className="adl-action-tag"
              rounded
              role="button"
              tabIndex={0}
              style={{ border: "1px solid " + action.border, borderRadius: "999px", fontSize: "0.9rem", fontWeight: 400, padding: "0.4rem 0.7rem", background: action.background, color: action.color, cursor: "pointer" }}
              onClick={() => openChartEntryDialog(rowData, action.value)}
              onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") openChartEntryDialog(rowData, action.value); }}
            />
          ))}
        </div>
      </div>
    );
  };

  const chartActionBody = (rowData: any) => {
    const occurrences = Array.isArray(rowData?.occurrences) ? rowData.occurrences : [rowData];
    return (
      <div className="flex flex-column gap-2" style={{ minWidth: "18rem" }}>
        {occurrences.map((occurrence: any) => (
          <div key={String(occurrence?.occurrenceKey || occurrence?.taskId || "adl-occurrence")}>
            {chartActionCardBody(occurrence)}
          </div>
        ))}
      </div>
    );
  };

  const todayChartedCount = entryDrafts.filter((entry) => String(entry?.status || "").trim()).length;
  const todayPendingCount = Math.max(entryDrafts.length - todayChartedCount, 0);
  const filteredHistoryEntryDrafts = useMemo(() => (
    historyStatusFilter === "missed"
      ? historyEntryDrafts.filter((entry: any) => entry?.missed === true || String(entry?.status || "").trim().toLowerCase() === "missed")
      : historyEntryDrafts
  ), [historyStatusFilter, historyEntryDrafts]);
  const historyChartedCount = filteredHistoryEntryDrafts.filter((entry) => String(entry?.status || "").trim()).length;
  const historyPendingCount = Math.max(filteredHistoryEntryDrafts.length - historyChartedCount, 0);
  const historyDateValue = getLosAngelesDateOnlyCalendarValue(historyDateKey);
  const historyMinDateValue = getLosAngelesDateOnlyCalendarValue(getLosAngelesHistoryEarliestDateString());
  const historyMaxDateValue = getLosAngelesDateOnlyCalendarValue(getLosAngelesTodayDateString());

  const historyTaskBody = (rowData: any) => (
    <div style={{ minWidth: 0 }}>
      {passTaskBody(rowData)}
      {String(rowData?.recordedAt || "").trim() ? (
        <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
          Recorded at: {new Intl.DateTimeFormat("en-US", {
            timeZone: "America/Los_Angeles",
            dateStyle: "medium",
            timeStyle: "short",
          }).format(new Date(rowData.recordedAt))}
        </div>
      ) : null}
      {Array.isArray(rowData?.statusHistory) && rowData.statusHistory.length > 1 ? (
        <Tag className="mt-2" value={String(rowData.statusHistory.length) + " chart events"} severity="info" rounded />
      ) : null}
    </div>
  );

  const historyChartActionBody = (rowData: any) => (
    <div className="flex flex-column gap-2" style={{ minWidth: "18rem" }}>
      {chartActionCardBody({
        ...rowData,
        correctionMode: true,
        dateKey: String(rowData?.dateKey || historyDateKey).trim(),
      })}
    </div>
  );

  if (userRoleType && !canUseCaregiverWorkspace) {
    return <Unauthorized />;
  }

  return (
    <div className="container-fluid p-3 caregiver-resident-adl-page">
      <Toast ref={toast} />
      <style>
        {`
          .caregiver-resident-adl-page .resident-workspace-tabs .p-tabview-nav-container {
            margin-bottom: 1rem;
          }
          .caregiver-resident-adl-page .resident-workspace-tabs .p-tabview-nav {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 0.55rem;
            padding: 0.45rem;
            border: 1px solid #dbe4f0;
            border-radius: 18px;
            background: linear-gradient(180deg, #f8fbff 0%, #f1f5f9 100%);
            width: 100%;
          }
          .caregiver-resident-adl-page .resident-workspace-tabs .p-tabview-nav li .p-tabview-nav-link {
            border: 1px solid #d7e0ea;
            border-radius: 14px;
            background: #ffffff;
            color: #475569;
            padding: 0.8rem 1rem;
            font-weight: 700;
          }
          .caregiver-resident-adl-page .resident-workspace-tabs .p-tabview-nav li.p-highlight .p-tabview-nav-link {
            border-color: #fdba74;
            background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 55%, #fef3c7 100%);
            color: #0f172a;
            box-shadow: 0 8px 18px rgba(249, 115, 22, 0.14);
          }
          .caregiver-resident-adl-page .resident-workspace-tabs .p-tabview-panels {
            padding: 0;
            background: transparent;
          }

          @media (max-width: 640px) {
            .caregiver-resident-adl-page .resident-workspace-tabs .p-tabview-nav-container {
              overflow: visible;
            }

            .caregiver-resident-adl-page .resident-workspace-tabs .p-tabview-nav {
              gap: 0.4rem;
            }

            .caregiver-resident-adl-page .resident-workspace-tabs .p-tabview-nav li {
              flex: 1 1 calc(50% - 0.25rem);
              min-width: 0;
            }

            .caregiver-resident-adl-page .resident-workspace-tabs .p-tabview-nav li .p-tabview-nav-link {
              width: 100%;
              min-height: 2.75rem;
              padding: 0.6rem 0.65rem;
              white-space: normal;
              text-align: center;
              line-height: 1.25;
              justify-content: center;
            }
          }

          .caregiver-resident-adl-page .adl-action-tag {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-height: 2.1rem;
          }
          .caregiver-resident-adl-page .adl-action-tag .p-tag-value {
            color: inherit !important;
            font-size: inherit !important;
            font-weight: inherit !important;
          }
          .caregiver-resident-adl-page .chart-dialog-action .p-button-label {
            color: inherit !important;
          }
          .caregiver-resident-adl-page .chart-dialog-action {
            color: #334155 !important;
          }
          .caregiver-resident-adl-page .p-selectbutton .p-button {
            color: #334155 !important;
          }
          .caregiver-resident-adl-page .p-selectbutton .p-button.p-highlight {
            color: #0f172a !important;
          }
        `}
      </style>
      <Dialog
        visible={showChartEntryDialog}
        modal
        onHide={closeChartEntryDialog}
        header={renderSurfaceHeader(
          chartEntryDraft?.actionLabel ? `${chartEntryDraft.actionLabel} ADL` : "Chart ADL",
          adlNoteOnlyActionValues.has(String(chartEntryDraft?.value || "").trim()) ? "Add a note for this action." : "Add support, parameters, notes, and chart time for this pass.",
          "pi pi-file-edit",
        )}
        style={adlChartDialogStyle}
        footer={renderDialogFooter({ loading: saving, onCancel: closeChartEntryDialog, onSubmit: applyChartEntryDialog, submitLabel: "Save", cancelButtonProps: { className: "chart-dialog-action" }, submitButtonProps: { className: "chart-dialog-action" } })}
      >
        <div style={{ display: "grid", gap: "0.95rem" }}>
          {!adlNoteOnlyActionValues.has(String(chartEntryDraft?.value || "").trim()) && Array.isArray(chartEntryDraft?.subTypes) && chartEntryDraft.subTypes.length ? (
            <div style={{ order: 2 }}>
              <label className="block mb-2 font-semibold">Sub Status</label>
              <Dropdown
                value={chartEntryDraft?.selectedSubTypeCode || ""}
                options={chartEntryDraft.subTypes.map((subType: any) => ({
                  label: `${String(subType?.code || "").trim().toUpperCase()} ${String(subType?.label || "").trim()}`.trim(),
                  value: String(subType?.code || "").trim().toUpperCase(),
                }))}
                placeholder="Select sub status"
                className="w-full"
                onChange={(event) => setChartEntryDraft((current: any) => ({
                  ...(current || {}),
                  selectedSubTypeCode: String(event.value || ""),
                  selectedSubTypeCodes: event.value ? [String(event.value || "")] : [],
                }))}
              />
            </div>
          ) : null}
          {chartEntryDraft?.value === "hospitalized" && chartEntryDraft?.correctionMode !== true ? (
            <div className="flex align-items-center gap-2" style={{ order: 2 }}>
              <Checkbox
                inputId="caregiverAdlHospitalizedAllPasses"
                checked={applyHospitalizedToAllForDay}
                onChange={(event) => setApplyHospitalizedToAllForDay(!!event.checked)}
              />
              <label htmlFor="caregiverAdlHospitalizedAllPasses">Apply Hospitalized to all scheduled passes today</label>
            </div>
          ) : null}
          {!adlNoteOnlyActionValues.has(String(chartEntryDraft?.value || "").trim()) && isAdlChartOutsideOneHourWindow(chartEntryDraft) ? (
            <div style={{ order: 2 }}>
              <div style={{ border: "1px solid #dbeafe", background: "#f8fbff", borderRadius: "16px", padding: "0.85rem 0.95rem" }}>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Choose administration time</div>
                <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                  Choose whether to save it at the scheduled time, the current time, or an exact charting time.
                </div>
                <div className="mt-2" style={{ border: "1px solid #fde68a", background: "#fffbeb", borderRadius: "12px", padding: "0.8rem 0.9rem", color: "#92400e", fontSize: "0.88rem", lineHeight: 1.5 }}>
                  This ADL item is being charted about {getAdlChartTimingDifferenceHours(chartEntryDraft).toFixed(1)} hours after the scheduled time. Choose whether to save it at the scheduled time or the current time.
                </div>
                <div className="mt-2 grid">
                  {[
                    { value: "scheduled", label: "Recorded at scheduled time", helper: formatAdlOccurrenceLabel(chartEntryDraft) },
                    { value: "current", label: "Recorded at current time", helper: new Intl.DateTimeFormat("en-US", { timeZone: "America/Los_Angeles", hour: "numeric", minute: "2-digit" }).format(new Date()) },
                    { value: "exact", label: "Exact time", helper: "Choose a specific charting time below." },
                  ].map((option) => {
                    const selected = chartEntryDraft?.chartTimeMode === option.value;
                    const disabled = option.value === "scheduled" && isAdlOccurrenceScheduledInFuture(chartEntryDraft);
                    return (
                      <div className="col-12 md:col-6" key={option.value}>
                        <button
                          type="button"
                          disabled={disabled}
                          onClick={() => setChartEntryDraft((current: any) => ({ ...(current || {}), chartTimeMode: option.value }))}
                          style={{ width: "100%", minHeight: "5.25rem", textAlign: "left", border: `1px solid ${selected ? "#2563eb" : "#dbe3ef"}`, borderRadius: "14px", padding: "0.8rem 0.9rem", background: disabled ? "#f8fafc" : selected ? "#eff6ff" : "#fff", color: disabled ? "#94a3b8" : "#0f172a", cursor: disabled ? "not-allowed" : "pointer" }}
                        >
                          <div style={{ fontWeight: 700 }}>{option.label}</div>
                          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>{option.helper}</div>
                        </button>
                      </div>
                    );
                  })}
                  <div className="col-12 md:col-6">
                    <div style={{ minHeight: "5.25rem", border: "1px solid #dbe3ef", borderRadius: "14px", padding: "0.8rem 0.9rem", background: "#fff" }}>
                      <label className="block mb-2" style={{ fontWeight: 700, color: "#0f172a" }}>Exact charting time</label>
                      <Dropdown
                        value={chartEntryDraft?.exactChartTime || ""}
                        options={adlScheduleTimeOptions.filter((timeOption: any) => !isAdlOccurrenceScheduledInFuture({ occurrenceLabel: timeOption.value }))}
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select exact time"
                        disabled={chartEntryDraft?.chartTimeMode !== "exact"}
                        className="w-full"
                        onChange={(event) => setChartEntryDraft((current: any) => ({ ...(current || {}), exactChartTime: String(event.value || "") }))}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          {!adlNoteOnlyActionValues.has(String(chartEntryDraft?.value || "").trim()) ? <div style={{ ...appInnerCardStyle, padding: "1rem", order: 1 }}>
            <div className="mb-3" style={{ fontWeight: 800, color: "#0f172a" }}>Charting parameters</div>
            <div className="grid align-items-start">
              <div className="col-12 md:col-6">
                <label className="block mb-2 font-semibold">Support required *</label>
                <Dropdown
                  value={chartEntryDraft?.status || ""}
                  options={supportLevelOptions}
                  placeholder="Select support required"
                  className="w-full"
                  onChange={(event) => setChartEntryDraft((current: any) => ({ ...(current || {}), status: String(event.value || "") }))}
                />
              </div>
              {Array.isArray(chartEntryDraft?.chartingParameters) && chartEntryDraft.chartingParameters.length ? chartEntryDraft.chartingParameters.map((parameter: any) => {
                const parameterId = String(parameter?.parameterId || "").trim();
                const parameterValue = chartEntryDraft?.parameterValues?.[parameterId] ?? null;
                return (
                  <div className="col-12 md:col-6" key={parameterId}>
                    <label className="block mb-2">{parameter.label}</label>
                    {parameter.type === "number" ? (
                      <InputNumber
                        value={typeof parameterValue === "number" ? parameterValue : parameterValue ? Number(parameterValue) : null}
                        className="w-full"
                        onValueChange={(event) => setChartEntryDraft((current: any) => ({ ...(current || {}), parameterValues: { ...(current?.parameterValues || {}), [parameterId]: event.value ?? "" } }))}
                      />
                    ) : (
                      <Dropdown
                        value={parameterValue || ""}
                        options={(Array.isArray(parameter?.options) ? parameter.options : []).map((option: string) => ({ label: option, value: option }))}
                        placeholder="Select one"
                        className="w-full"
                        onChange={(event) => setChartEntryDraft((current: any) => ({ ...(current || {}), parameterValues: { ...(current?.parameterValues || {}), [parameterId]: event.value || "" } }))}
                      />
                    )}
                  </div>
                );
              }) : <div className="col-12" style={appMutedTextStyle}>No additional parameters configured for this ADL item.</div>}
            </div>
          </div> : null}
          <div style={{ order: 3 }}>
            <label className="block mb-2 font-semibold">Note{chartEntryDraft?.value !== "recorded" ? " *" : ""}</label>
            <InputTextarea
              value={chartEntryDraft?.note || ""}
              onChange={(event) => setChartEntryDraft((current: any) => ({ ...(current || {}), note: event.target.value }))}
              rows={4}
              autoResize
              className="w-full"
            />
          </div>
        </div>
      </Dialog>
      <Dialog
        visible={showReportDialog}
        style={{ width: "32rem", maxWidth: "96vw" }}
        modal
        onHide={() => {
          if (!exportingReport) {
            setShowReportDialog(false);
          }
        }}
        header={renderSurfaceHeader(
          "Generate ADL Report",
          "Choose a month, the last 7 days, or a custom range of up to 31 days.",
          "pi pi-calendar",
        )}
      >
        <div className="pt-2 flex flex-column gap-3">
          <div style={{ ...appNeutralPanelStyle, padding: "1rem" }}>
            <div className="flex align-items-center gap-2 flex-wrap mb-3">
              <Button
                type="button"
                label="Monthly"
                outlined={reportRangeMode !== "monthly"}
                rounded
                severity={reportRangeMode === "monthly" ? "success" : "warning"}
                onClick={() => setReportRangeMode("monthly")}
              />
              <Button
                type="button"
                label="Custom range"
                outlined={reportRangeMode !== "custom"}
                rounded
                severity={reportRangeMode === "custom" ? "success" : "warning"}
                onClick={() => setReportRangeMode("custom")}
              />
              <Button
                type="button"
                label="Last 7 days"
                outlined={reportRangeMode !== "last7Days"}
                rounded
                severity={reportRangeMode === "last7Days" ? "success" : "warning"}
                onClick={() => setReportRangeMode("last7Days")}
              />
            </div>
            {reportRangeMode === "monthly" ? (
              <>
                <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.5rem" }}>Report month</div>
                <Dropdown
                  id="caregiverAdlReportMonth"
                  value={selectedReportMonth}
                  options={reportMonthOptions}
                  onChange={(event) => setSelectedReportMonth(String(event.value || ""))}
                  className="w-full"
                />
                <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                  The monthly report generates the full selected month.
                </div>
              </>
            ) : reportRangeMode === "custom" ? (
              <div className="grid">
                <div className="col-12">
                  <label className="block mb-2 font-semibold">Start date</label>
                  <Calendar value={getLosAngelesDateOnlyCalendarValue(reportStartDate)} onChange={(event) => setReportStartDate(formatLosAngelesDateOnlyFromCalendar(event.value as Date | null))} dateFormat="mm/dd/yy" showIcon readOnlyInput className="w-full" />
                </div>
                <div className="col-12">
                  <label className="block mb-2 font-semibold">End date</label>
                  <Calendar value={getLosAngelesDateOnlyCalendarValue(reportEndDate)} onChange={(event) => setReportEndDate(formatLosAngelesDateOnlyFromCalendar(event.value as Date | null))} dateFormat="mm/dd/yy" showIcon readOnlyInput className="w-full" />
                </div>
                <div className="col-12" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>Custom reports support up to 31 calendar days.</div>
              </div>
            ) : (
              <div style={{ ...appMutedTextStyle, lineHeight: 1.6 }}>
                Includes today and the previous 6 days, based on the America/Los_Angeles calendar.
              </div>
            )}
            <div className="mt-3">
              <label style={{ display: "flex", alignItems: "center", gap: "0.7rem", cursor: "pointer" }}>
                <Checkbox
                  inputId="caregiver-adl-blank-report-checkbox"
                  checked={blankAdlReport}
                  onChange={(event) => setBlankAdlReport(Boolean(event.checked))}
                />
                <span style={{ fontWeight: 700, color: "#0f172a" }}>Generate blank ADL report</span>
              </label>
              <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                Keep tasks, schedules, resident details, and legends, but leave daily chart cells blank.
              </div>
            </div>
          </div>
          <div className="flex flex-wrap justify-content-end gap-2">
            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              outlined
              disabled={exportingReport}
              onClick={() => setShowReportDialog(false)}
            />
            <Button
              type="button"
              label="Download"
              severity="info"
              outlined
              rounded
              loading={exportingReport}
              onClick={() => { void handleDownloadReport(); }}
            />
          </div>
        </div>
      </Dialog>
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <div className="flex align-items-start justify-content-between gap-3 flex-wrap">
          {renderSurfaceHeader(
            `${adlData?.residentName || "Resident"} - ADL Tracking`,
            "Chart today's ADL tasks for this resident. Care plan setup stays provider managed, and caregivers only chart the published ADL list.",
            "pi pi-check-square",
          )}
          <div className="flex gap-2 flex-wrap justify-content-end">
            <Button
              type="button"
              label="Generate ADL Report"
              icon="pi pi-file"
              rounded
              severity="warning"
              disabled={!resolvedHomeId}
              onClick={() => setShowReportDialog(true)}
            />
            <Button
              type="button"
              label="Search Care Plan"
              rounded
              outlined
              severity="warning"
              onClick={() => navigate(sharedMode
                ? RouterAddress.getSharedResidentCarePlanPagePath(String(homeId || ""), String(residentId || ""))
                : RouterAddress.getCaregiverResidentCarePlanPagePath(String(residentId || "")))}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-content-center p-5">
            <ProgressSpinner style={{ width: "48px", height: "48px" }} />
          </div>
        ) : (
          <>
            <TabView activeIndex={activeTabIndex} onTabChange={(event) => setActiveTabIndex(event.index)} className="resident-workspace-tabs">
              <TabPanel header={`Today's Pass (${entryDrafts.length})`}>
                <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
                  <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                    <div>
                      <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>Today&apos;s Pass</div>
                      <div className="mt-1" style={{ ...appMutedTextStyle, maxWidth: "40rem" }}>
                        Review today's ADL tasks and chart what happened for each one.
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap align-items-center justify-content-end">
                      <Tag value={`${entryDrafts.length} total`} severity="info" />
                      <Tag value={`${todayChartedCount} charted`} severity={todayChartedCount > 0 ? "success" : "secondary"} />
                      <Tag value={`${todayPendingCount} pending`} severity={todayPendingCount > 0 ? "warning" : "success"} />
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2 flex-wrap align-items-center justify-content-end">
                    <span style={{ ...appMutedTextStyle, fontWeight: 700 }}>Filter by</span>
                    <Dropdown
                      value={todayPassFilter}
                      options={adlPassFilterOptions}
                      onChange={(event) => setTodayPassFilter(event.value)}
                      style={{ minWidth: "10rem" }}
                    />
                  </div>
                  <div className="mt-3">
                    <div className="block md:hidden">
                      <div className="flex flex-column gap-3">
                        {groupedTodayEntryDrafts.length ? groupedTodayEntryDrafts.map((row: any, index: number) => (
                          <div key={row?.taskId || `adl-entry-${index}`} style={{ ...appInnerCardStyle, padding: "0.9rem 1rem" }}>
                            {passTaskBody(row)}
                            <div className="mt-3">{chartSummaryBody(row)}</div>
                            <div className="mt-3">{chartActionBody(row)}</div>
                          </div>
                        )) : <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "1rem 0.5rem" }}>No ADL tasks are published for this resident yet.</div>}
                      </div>
                    </div>
                    <div className="hidden md:block">
                      <DataTable value={groupedTodayEntryDrafts} emptyMessage="No ADL tasks are published for this resident yet." responsiveLayout="scroll" size="small" stripedRows>
                        <Column header="Task" body={passTaskBody} style={{ width: "30%", maxWidth: "30%", minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }} />
                        <Column header="Status" body={chartSummaryBody} style={{ minWidth: "17rem" }} />
                        <Column header="Actions" body={chartActionBody} style={{ minWidth: "20rem" }} />
                      </DataTable>
                    </div>
                  </div>
                </div>
              </TabPanel>
              <TabPanel header={`ADL List (${adlListRows.length})`}>
                <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
                  <div className="flex align-items-start justify-content-between gap-2 flex-wrap mb-3">
                    <div>
                      <div style={{ fontWeight: 800, color: "#0f172a", fontSize: "1rem" }}>ADL List</div>
                      <div style={{ ...appMutedTextStyle, marginTop: "0.25rem", lineHeight: 1.6 }}>
                        Review the published ADL tasks and follow the listed support instructions while charting today&apos;s pass.
                      </div>
                    </div>
                  </div>
                  <div className="block md:hidden">
                    <div className="flex flex-column gap-3">
                      {adlListRows.length ? adlListRows.map((row: any, index: number) => (
                        <div key={row?.taskId || `adl-list-${index}`} style={{ ...appInnerCardStyle, padding: "0.9rem 1rem" }}>
                          {adlTaskBody(row)}
                          <div className="mt-3">{parametersBody(row)}</div>
                          <div className="mt-3">
                            <div style={{ ...appMutedTextStyle, fontWeight: 700 }}>Support Level</div>
                            <div style={{ color: "#0f172a", marginTop: "0.2rem" }}>{row?.supportLevel || "Not set"}</div>
                          </div>
                        </div>
                      )) : <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "1rem 0.5rem" }}>No ADL tasks are published for this resident yet.</div>}
                    </div>
                  </div>
                  <div className="hidden md:block">
                    <DataTable value={adlListRows} emptyMessage="No ADL tasks are published for this resident yet." responsiveLayout="scroll" size="small" stripedRows>
                      <Column header="Task" body={adlTaskBody} style={{ width: "30%", maxWidth: "30%", minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }} />
                      <Column header="Parameters" body={parametersBody} style={{ minWidth: "18rem" }} />
                      <Column field="supportLevel" header="Support Level" style={{ minWidth: "14rem" }} />
                    </DataTable>
                  </div>
                </div>
              </TabPanel>
            </TabView>
          </>
        )}
      </div>
    </div>
  );
};

const CaregiverResidentAdlPage = () => <CaregiverResidentAdlPageContent />;

export default CaregiverResidentAdlPage;
